/**
 * BLOCK 100 Part E-5 — platform-wide timezone-aware display layer.
 *
 * Storage is UTC (ISO 8601 with offset) in the DB. The browser auto-detects
 * the viewer's local zone (Intl), and a manual override in Settings wins when
 * set. Every timestamp on the platform renders through these helpers so date
 * and time stay paired and correct in the viewer's zone.
 */
import { useEffect, useState } from "react";

const TZ_KEY = "ae_tz";
export const TZ_EVENT = "ae-tz-changed";

export function getActiveTimezone() {
  try {
    const o = localStorage.getItem(TZ_KEY);
    if (o && o !== "auto") return o;
  } catch {
    /* ignore */
  }
  return detectTimezone();
}

export function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function isAutoTimezone() {
  try {
    const o = localStorage.getItem(TZ_KEY);
    return !o || o === "auto";
  } catch {
    return true;
  }
}

export function setActiveTimezone(tz) {
  try {
    if (!tz || tz === "auto") localStorage.removeItem(TZ_KEY);
    else localStorage.setItem(TZ_KEY, tz);
    window.dispatchEvent(new Event(TZ_EVENT));
  } catch {
    /* ignore */
  }
}

export function listTimezones() {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch {
    /* ignore */
  }
  return [
    "America/New_York", "America/Chicago", "America/Denver", "America/Phoenix",
    "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu", "UTC",
  ];
}

function _parse(iso) {
  if (!iso) return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/** Date + time in the active zone, e.g. "Aug 14, 2026, 5:12 PM EDT". */
export function fmtDateTime(iso, { withZone = true } = {}) {
  const d = _parse(iso);
  if (!d) return "—";
  const opts = {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
    timeZone: getActiveTimezone(),
  };
  if (withZone) opts.timeZoneName = "short";
  try {
    return new Intl.DateTimeFormat(undefined, opts).format(d);
  } catch {
    return d.toISOString();
  }
}

/** Date only in the active zone (use ONLY where a time is genuinely irrelevant). */
export function fmtDate(iso) {
  const d = _parse(iso);
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric", month: "short", day: "numeric", timeZone: getActiveTimezone(),
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** React hook: re-renders a component when the active timezone changes. */
export function useActiveTimezone() {
  const [tz, setTz] = useState(getActiveTimezone());
  useEffect(() => {
    const h = () => setTz(getActiveTimezone());
    window.addEventListener(TZ_EVENT, h);
    return () => window.removeEventListener(TZ_EVENT, h);
  }, []);
  return tz;
}
